/* eslint-disable no-undef */
import React, { useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Layout, DatePicker } from 'antd'
import dayjs from 'dayjs'
import sensors from '../../utils/sensors'
import Viewer from '../components/ViewerV2'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { fetchPhotoRestoreStatus, setLoading } from '../../actions/main'
import {
  setDate,
  setPathways,
  setCheckedMembers,
  fetchMembers,
  fetchPathwaysByMembers,
  mapCheckUserDetail,
  viewPhoto
} from '../../actions/pathway'

import UserList from './UserList'
import UserDetail from './UserDetail'
import Map from './MapContainer'
import CONFIG from '../../constants/config'
import * as MapUtils from '../../utils/mapUtils'
import { capturePromiseException } from '../../utils/sentryUtils'
import member_location_entry_btn from '../memberLocation/images/member-location-entry-btn.png'

import PageHeader from '@/components/PageHeader'
const { Content } = Layout

function Pathway() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const viewerRef = useRef(null)

  // Redux state
  const date = useAppSelector((state) => state.pathway.date)
  const currentUser = useAppSelector((state) => state.pathway.currentUser)
  const viewerPhotos = useAppSelector((state) => state.pathway.viewerPhotos)
  const groupId = useAppSelector((state) => state.group.groupId)
  const config = useAppSelector((state) => state.main.config)
  const isVip = useAppSelector((state) => state.group.isVip)
  const showMemberLocation = useAppSelector(
    (state) => state.group.show_member_location
  )

  // 请求成员数据
  const handleFetchMembers = useCallback(
    (dateParam = date) => {
      if (!dateParam) return
      let takeMembers: { userID: string | number }[] = []
      let memberIds: (string | number)[] = []
      dispatch(setLoading(true))
      dispatch(
        fetchMembers({
          groupID: groupId,
          date: dateParam.format(CONFIG.dateFormat)
        })
      )
        .then((memberData) => {
          takeMembers = memberData.peoplesTake
          memberIds = takeMembers.slice(0, 5).map((member) => member.userID)
          if (currentUser) {
            // 如果正在查看用户详情，修改日期后，则只请求当前用户的路线
            memberIds = [currentUser.userID]
          }
          dispatch(setCheckedMembers(memberIds))
          return dispatch(
            fetchPathwaysByMembers({
              groupID: groupId,
              date: dateParam.format(CONFIG.dateFormat),
              users: memberIds
            })
          )
        })
        .then((pathwayData) => {
          if (window.MapInstance && pathwayData.peoples.length) {
            const pathways = pathwayData.peoples
            dispatch(setPathways(pathways))
            MapUtils.drawPathsV2({
              pathways,
              checkedMembers: memberIds,
              createInfoWindow: (pathIndexInMap, markerIndex) =>
                MapUtils.createLocationInfoWindowV2({
                  pathway: pathways[pathIndexInMap],
                  markerIndex,
                  onButtonClick: () => {
                    dispatch(
                      mapCheckUserDetail(
                        pathways[pathIndexInMap],
                        viewerRef.current
                      )
                    )
                    sensors.track('web_pathway_homepage_click', {
                      clickItem: 'routeNodeDetail',
                      groupID: groupId,
                      locationName:
                        pathways[pathIndexInMap]?.locations?.[markerIndex]
                          ?.texts?.addr,
                      photoNum:
                        pathways[pathIndexInMap]?.locations?.[markerIndex]
                          ?.photos?.length || 0
                    })
                  },
                  onViewPhoto: (photoIndex) => {
                    dispatch(
                      viewPhoto(
                        pathways[pathIndexInMap],
                        markerIndex,
                        photoIndex,
                        viewerRef.current
                      )
                    )
                    sensors.track('web_pathway_homepage_click', {
                      clickItem: 'routeNodePic',
                      groupID: groupId,
                      locationName:
                        pathways[pathIndexInMap]?.locations?.[markerIndex]
                          ?.texts?.addr,
                      photoNum:
                        pathways[pathIndexInMap]?.locations?.[markerIndex]
                          ?.photos?.length || 0
                    })
                  },
                  canCheckDetail: currentUser ? false : true
                })
            })
          } else {
            window.MapInstance && window.MapInstance.clearMap()
          }
          dispatch(setLoading(false))
        })
        .catch((err) => {
          capturePromiseException(err)
          dispatch(setLoading(false))
        })
    },
    [dispatch, groupId, currentUser, date]
  )

  // 日期变更
  const handleDateChange = useCallback(
    async (dateValue, dateString) => {
      if (currentUser) {
        sensors.track('web_pathway_memberdetail_click', {
          clickItem: 'filterTime',
          groupID: groupId
        })
      } else {
        sensors.track('web_pathway_homepage_click', {
          clickItem: 'filterTime',
          groupID: groupId
        })
      }

      // vip团队且时间超90天，检查解冻状态： 完成 ? 提交选择时间 : 无法提交时间
      // 否则 提交选择时间
      if (
        isVip &&
        dayjs().diff(dateValue, 'days', true) >
          parseInt(config.normal_group_data_limit_days || 90)
      ) {
        const restoreStatus = await dispatch(
          fetchPhotoRestoreStatus({
            groupID: groupId
          })
        )
        if (restoreStatus.status === 0) {
          dispatch(setDate(dateValue))
          handleFetchMembers(dateValue)
        }
      } else {
        dispatch(setDate(dateValue))
        handleFetchMembers(dateValue)
      }
    },
    [
      dispatch,
      groupId,
      currentUser,
      isVip,
      config.normal_group_data_limit_days,
      handleFetchMembers
    ]
  )

  // componentDidMount
  useEffect(() => {
    const group_id = localStorage.getItem('group_id')
    if (!group_id) {
      navigate(`/choose_group`, { replace: true })
    }
  }, [])

  const daysLimit = parseInt(config.normal_group_data_limit_days || 90)
  const disabledDate = (current) => {
    if (isVip) {
      return current > dayjs()
    } else {
      return (
        current > dayjs() ||
        current <
          dayjs()
            .add(0 - daysLimit, 'days')
            .startOf('day')
      )
    }
  }

  const FilterForm = (
    <DatePicker
      style={{ width: '280px' }}
      value={date}
      allowClear={false}
      disabledDate={disabledDate}
      onChange={handleDateChange}
    />
  )

  return (
    <Layout className="Pathway">
      <PageHeader
        className="Header"
        ghost={false}
        title="拍照路线"
        extra={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* 查看人员实时定位按钮 - 根据showMemberLocation控制显示 */}
            {showMemberLocation && (
              <div
                style={{
                  marginBottom: 10,
                  marginRight: 20,
                  cursor: 'pointer'
                }}
                onClick={() => {
                  sensors.track('web_pathway_homepage_click', {
                    groupID: groupId,
                    clickItem: 'realTimeLocation',
                    locationName: '拍照路线页面',
                    track_sign:
                      'aworkgroup.b7665.action.web_pathway_homepage_click',
                    requirement_id: 322
                  })
                  navigate('/member/location')
                }}
              >
                <img
                  style={{ height: '40px' }}
                  src={member_location_entry_btn}
                />
              </div>
            )}
            {FilterForm}
          </div>
        }
      />
      <Content className="content">
        <Map afterMapInit={handleFetchMembers} />
        {currentUser ? (
          <UserDetail viewer={viewerRef.current} />
        ) : (
          <UserList viewer={viewerRef.current} />
        )}
        <Viewer
          ref={viewerRef}
          canDelete={false}
          canRectify={false}
          canAiSecurity={false}
          photos={viewerPhotos}
        />
      </Content>
    </Layout>
  )
}

export default Pathway
